import React, { forwardRef, useCallback, useImperativeHandle } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

/*
 * Wraps existing upload-button markup with native drag-and-drop support, on top of the
 * click-to-browse behavior react-dropzone already provides for free. Callers keep their
 * existing button/icon as `children` - this just adds a visible drop target and normalizes
 * both paths (click-select and drag-drop) to a single `File` handed back via `onFileSelected`.
 *
 * `compact` skips the pronounced box (border/padding/min-height/helper text) for contexts
 * where the wrapped button sits inline in a tight header row rather than its own content pane -
 * drag-and-drop still works, it just doesn't claim a large visible drop target.
 *
 * A parent that needs to open the file dialog programmatically (e.g. a confirm-dialog flow
 * that isn't a direct click on this component) can pass a ref and call `ref.current.open()`.
 */
const DocumentDropzone = forwardRef(({
    onFileSelected,
    accept,
    disabled = false,
    compact = false,
    className = '',
    children
}, ref) => {
    const { t } = useTranslation()

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles?.length > 0) {
            onFileSelected(acceptedFiles[0])
        }
    }, [onFileSelected])

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        accept,
        disabled,
        multiple: false
    })

    useImperativeHandle(ref, () => ({ open }), [open])

    return (
        <div
            {...getRootProps()}
            className={className}
            style={compact ? {
                borderRadius: '4px',
                outline: isDragActive ? '2px dashed #607d8b' : '2px dashed transparent',
                outlineOffset: '2px',
                transition: 'outline-color 0.15s ease'
            } : {
                borderRadius: '8px',
                border: `1px dashed ${isDragActive ? '#607d8b' : '#D0D2D6'}`,
                backgroundColor: isDragActive ? '#EEF2F3' : '#FCFCFC',
                padding: '32px 16px',
                minHeight: '140px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'border-color 0.15s ease, background-color 0.15s ease'
            }}
        >
            <input {...getInputProps()} />
            {children}
            {!compact && !disabled && (
                <p className='text-xs text-slate-500'>
                    {isDragActive ? t('drop_file_here') : t('drag_and_drop_file_here')}
                </p>
            )}
        </div>
    )
})

export default DocumentDropzone
